import { useEffect } from 'react';
import useBatchState from './useBatchState';

export const ERROR_TYPE = { valid: 'valid', inValid: 'inValid', noValidation: 'noValidation' };

/*
 * Relying on the HTML5 validation api isn't really useful when you want to be able to listen to the validity state of
 * an input, there are too many gotchas, this use validation hook aims to normalise the behaviors and publish the correct
 * valid state. We also have a 3rd state where we don't want to show any validation at all, which isn't covered in the api.
 *
 * The approach below is to leverage the formNoValidate attribute and the default form validation. ValidityState.current can
 * either be `true`, `false`, or `undefined` so any input can listen to that state and display appropriate validation styles and
 * formNoValidate is set to either true or false based on whether the input should be valid, invalid or rely on the HTML 5
 * validity api.
 * */

/**
 *
 * @param {Object} inputRef - the ref of the input
 * @param {String} value - the inputs current value
 * @param {ERROR_TYPE} overrideValidation - Override validation, ignoring any built in validation
 * @param {Array} dependencies - An array of input attributes that the validation relies on
 * @param {String} requiredMessage - Custom required message.
 * @param {String} errorMessage - Custom error message.
 * @param {Boolean} errorOnly - if `true`, `validity.valid` will either be `undefined` or `false` you wont receive `true` for `validity.valid`
 * @returns {Object} - the inputs current validity state (matches the HTML5 api)
 */

export default (
	{ inputRef, value, overrideValidation, requiredMessage, errorMessage: errorMessageParam, errorOnly },
	dependencies = {}
) => {
	const dependenciesArray = Object.values(dependencies);
	const [{ errorMessage, validity }, setState] = useBatchState({
		errorMessage: '',
		validity: (inputRef.current && inputRef.current.validity) || {}
	});

	useEffect(
		() => {
			inputRef.current.setCustomValidity('');
			// its no possible to clone the validity state so it can be modified, so we have to do a 1 to 1 map
			const inputState = inputRef.current.validity;
			const validity = {
				badInput: inputState.badInput,
				customError: inputState.customError,
				patternMismatch: inputState.patternMismatch,
				rangeOverflow: inputState.rangeOverflow,
				rangeUnderflow: inputState.rangeUnderflow,
				stepMismatch: inputState.stepMismatch,
				tooLong: inputState.tooLong,
				tooShort: inputState.tooShort,
				typeMismatch: inputState.typeMismatch,
				valid: inputState.valid,
				valueMissing: inputState.valueMissing
			};

			if (inputRef.current) {
				if (overrideValidation !== undefined) {
					if (overrideValidation === ERROR_TYPE.valid) {
						inputRef.current.setCustomValidity('');
						inputRef.current.formNoValidate = true;
						validity.valid = errorOnly ? undefined : true;
						setState({ errorMessage: '', validity });
					}

					if (overrideValidation === ERROR_TYPE.inValid) {
						inputRef.current.setCustomValidity('error');
						inputRef.current.formNoValidate = false;
						validity.valid = false;
						setState({ errorMessage: '', validity });
					}

					if (overrideValidation === ERROR_TYPE.noValidation) {
						inputRef.current.setCustomValidity('');
						inputRef.current.formNoValidate = true;
						validity.valid = undefined;
						setState({ errorMessage: '', validity });
					}
				}

				if (overrideValidation === undefined) {
					inputRef.current.setCustomValidity('');
					inputRef.current.formNoValidate = false;
					if (dependenciesArray.length === 0) {
						setState({ validity, errorType: 'no-validation' });
					} else {
						const { maxLength, minLength } = dependencies;
						const valLength = (value || '').length;
						let errorMessage = inputRef.current.validationMessage;
						if (validity.valid) validity.valid = errorOnly ? undefined : true;

						/**
						 * minLength and maxLength only validate after a user has input, we need to be able to validate min and max length
						 * without user changing the input. To fix this we perform manual validation of the min and max length
						 * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/tooShort
						 * https://developer.mozilla.org/en-US/docs/Web/API/ValidityState/tooLong
						 */
						if (minLength !== undefined && valLength < minLength) {
							validity.tooShort = true;
							validity.valid = false;
							errorMessage = `Please lengthen this text to ${minLength} characters or more (you are currently using ${valLength} characters).`;
						}

						if (maxLength !== undefined && valLength > maxLength) {
							validity.tooLong = true;
							validity.valid = false;
							errorMessage = `Please shorten this text to ${maxLength} characters or less (you are currently using ${valLength} characters).`;
						}
						if (inputRef.current.type === 'email' && validity.typeMismatch) {
							errorMessage = 'Please enter a valid email address.';
						}
						if (inputRef.current.validity.valueMissing) errorMessage = requiredMessage || 'This is a required field.';
						setState({ errorMessage, validity });
					}
				}
			}

			if (errorMessageParam) setState({ errorMessage: errorMessageParam });
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[inputRef, value, overrideValidation, requiredMessage, errorMessageParam, ...dependenciesArray]
	);

	return [validity, errorMessage];
};
